/**
 * ArmorSystem -- Manages equipped armor and damage reduction.
 *
 * Armor slots: helmet, chestplate, leggings, boots
 * Materials: iron, gold, diamond (each with different defense values)
 *
 * Defense points per piece:
 *   Helmet:     iron=2, gold=2, diamond=3
 *   Chestplate: iron=6, gold=5, diamond=8
 *   Leggings:   iron=5, gold=3, diamond=6
 *   Boots:      iron=2, gold=1, diamond=3
 *
 * Total max: iron=15, gold=11, diamond=20
 * Damage reduction: armorPoints / 25 (so 20 points = 80% reduction)
 */

// ============================================================
// ARMOR MATERIAL CONSTANTS
// ============================================================

/**
 * Defense values per material and slot, plus durability per material.
 */
export const ARMOR_MATERIALS = {
  iron: {
    helmet:     { defense: 2 },
    chestplate: { defense: 6 },
    leggings:   { defense: 5 },
    boots:      { defense: 2 },
    durability: 165,
  },
  gold: {
    helmet:     { defense: 2 },
    chestplate: { defense: 5 },
    leggings:   { defense: 3 },
    boots:      { defense: 1 },
    durability: 77,
  },
  diamond: {
    helmet:     { defense: 3 },
    chestplate: { defense: 8 },
    leggings:   { defense: 6 },
    boots:      { defense: 3 },
    durability: 363,
  },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Check if an inventory item is an armor piece.
 * An armor item has the `armorSlot` property set.
 * @param {Object|null} item
 * @returns {boolean}
 */
export function isArmorItem(item) {
  return item != null && item.armorSlot !== undefined;
}

/**
 * Create an armor item object ready for inventory use.
 *
 * @param {'helmet'|'chestplate'|'leggings'|'boots'} slot
 * @param {'iron'|'gold'|'diamond'} material
 * @param {number} itemId - The ItemId constant for this piece
 * @returns {{ itemId: number, armorSlot: string, material: string, defense: number, durability: number, maxDurability: number, count: 1 }}
 */
export function createArmor(slot, material, itemId) {
  const matData = ARMOR_MATERIALS[material];
  if (!matData || !matData[slot]) {
    throw new Error(`Invalid armor: ${material} ${slot}`);
  }
  return {
    itemId,
    armorSlot: slot,
    material,
    defense: matData[slot].defense,
    durability: matData.durability,
    maxDurability: matData.durability,
    count: 1,
  };
}

// ============================================================
// ARMOR SYSTEM CLASS
// ============================================================

export class ArmorSystem {
  constructor() {
    /**
     * Equipped armor slots. Each is either null or an armor item object.
     * @type {{ helmet: Object|null, chestplate: Object|null, leggings: Object|null, boots: Object|null }}
     */
    this.slots = {
      helmet: null,
      chestplate: null,
      leggings: null,
      boots: null,
    };
  }

  /**
   * Equip an armor item into the appropriate slot.
   * @param {'helmet'|'chestplate'|'leggings'|'boots'} slot
   * @param {Object} armorItem - Must have armorSlot, material, defense, durability, maxDurability
   * @returns {Object|null} The previously equipped armor in that slot (null if empty)
   */
  equip(slot, armorItem) {
    if (!this.slots.hasOwnProperty(slot)) {
      throw new Error(`Slot de armadura inv\u00e1lido: ${slot}`);
    }
    const old = this.slots[slot];
    this.slots[slot] = armorItem;
    return old;
  }

  /**
   * Remove and return armor from a slot.
   * @param {'helmet'|'chestplate'|'leggings'|'boots'} slot
   * @returns {Object|null} The removed armor, or null if slot was empty
   */
  unequip(slot) {
    if (!this.slots.hasOwnProperty(slot)) {
      throw new Error(`Slot de armadura inv\u00e1lido: ${slot}`);
    }
    const old = this.slots[slot];
    this.slots[slot] = null;
    return old;
  }

  /**
   * Get the armor item equipped in a given slot.
   * @param {'helmet'|'chestplate'|'leggings'|'boots'} slotName
   * @returns {Object|null}
   */
  getSlot(slotName) {
    return this.slots[slotName] || null;
  }

  /**
   * Calculate total armor points from all equipped pieces.
   * @returns {number} Sum of defense values (0-20 typically)
   */
  getArmorPoints() {
    let total = 0;
    for (const slot of Object.keys(this.slots)) {
      const piece = this.slots[slot];
      if (piece && piece.defense !== undefined) {
        total += piece.defense;
      }
    }
    return total;
  }

  /**
   * Get the damage multiplier based on current armor.
   * Formula: 1 - (armorPoints / 25), clamped to [0.2, 1.0].
   * With 20 armor points, multiplier is 0.2 (80% reduction).
   * @returns {number} Multiplier between 0.2 and 1.0
   */
  getDamageReduction() {
    const points = this.getArmorPoints();
    const multiplier = 1 - (points / 25);
    return Math.max(0.2, Math.min(1.0, multiplier));
  }

  /**
   * Apply armor reduction to raw damage.
   * @param {number} rawDamage - The incoming damage before armor
   * @returns {number} The reduced damage (minimum 1 if rawDamage > 0)
   */
  reduceDamage(rawDamage) {
    if (rawDamage <= 0) return 0;
    const reduced = rawDamage * this.getDamageReduction();
    // Always deal at least 1 damage if there was any incoming damage
    return Math.max(1, Math.round(reduced));
  }

  /**
   * Reduce durability of all equipped armor pieces by the given amount.
   * Pieces that reach 0 durability are removed (broken).
   * @param {number} amount - Durability to drain from each piece (default 1)
   */
  damageArmor(amount = 1) {
    for (const slot of Object.keys(this.slots)) {
      const piece = this.slots[slot];
      if (!piece) continue;
      if (piece.durability !== undefined) {
        piece.durability -= amount;
        if (piece.durability <= 0) {
          // Armor piece breaks
          this.slots[slot] = null;
        }
      }
    }
  }

  /**
   * Serialize armor state for save/load.
   * @returns {Object} Serializable object
   */
  serialize() {
    const data = {};
    for (const slot of Object.keys(this.slots)) {
      const piece = this.slots[slot];
      if (piece) {
        data[slot] = {
          itemId: piece.itemId,
          armorSlot: piece.armorSlot,
          material: piece.material,
          defense: piece.defense,
          durability: piece.durability,
          maxDurability: piece.maxDurability,
        };
      } else {
        data[slot] = null;
      }
    }
    return data;
  }

  /**
   * Restore armor state from saved data.
   * @param {Object} data - Previously serialized armor data
   */
  deserialize(data) {
    if (!data) return;
    for (const slot of Object.keys(this.slots)) {
      if (data[slot]) {
        this.slots[slot] = { ...data[slot], count: 1 };
      } else {
        this.slots[slot] = null;
      }
    }
  }
}
